/**
 * scripts-components-drawer.js
 * -----------------------------------------------------------------------
 * Generic slide-in drawer built on the native <dialog> element so ESC-to-
 * close, focus trapping and the ::backdrop come for free from the
 * platform. `<am-drawer>` only orchestrates the closing transition (native
 * dialogs close instantly, so we delay the real `.close()` call until our
 * CSS transition finishes) and wires up open/close triggers.
 *
 * Markup contract:
 *   <am-drawer id="cart-drawer">
 *     <dialog class="drawer__dialog" aria-label="Cart">
 *       ...
 *       <button data-drawer-close>Close</button>
 *     </dialog>
 *   </am-drawer>
 *
 *   <button data-drawer-open="cart-drawer">Open cart</button>
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var CLOSE_TRANSITION_FALLBACK_MS = 400;

  class AmDrawerElement extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this._handleCancel = this._handleCancel.bind(this);
      this._handleBackdropClick = this._handleBackdropClick.bind(this);

      this.dialog.addEventListener('cancel', this._handleCancel);
      this.dialog.addEventListener('click', this._handleBackdropClick);
      this.querySelectorAll('[data-drawer-close]').forEach(
        function (button) {
          button.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );
    }

    _handleCancel(event) {
      // Intercept the native Escape-triggered close so it plays our
      // closing transition instead of disappearing instantly.
      event.preventDefault();
      this.close();
    }

    _handleBackdropClick(event) {
      var rect = this.dialog.getBoundingClientRect();
      var clickedInsideDialogBox =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!clickedInsideDialogBox) this.close();
    }

    open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      this.dispatchEvent(new CustomEvent('am:drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      var dialog = this.dialog;
      var self = this;

      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        dialog.close();
        self.dispatchEvent(new CustomEvent('am:drawer:close', { bubbles: true }));
        return;
      }

      dialog.classList.add('is-closing');

      var finish = function () {
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
        self.dispatchEvent(new CustomEvent('am:drawer:close', { bubbles: true }));
      };

      dialog.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, CLOSE_TRANSITION_FALLBACK_MS);
    }
  }

  if (!customElements.get('am-drawer')) {
    customElements.define('am-drawer', AmDrawerElement);
  }

  // Delegated trigger wiring so any button anywhere in the DOM can open a
  // drawer without each component needing its own listener.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    var drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
})();
